export function formatHoursToTime(hours, { hoursOnly = false } = {}) {
  // Convert input to number if it's a string
  const numericHours = typeof hours === "string" ? parseFloat(hours) : hours;

  // Handle invalid input
  if (isNaN(numericHours)) {
    return null;
  }

  const isNegative = numericHours < 0;
  const absHours = Math.abs(numericHours);

  // If hoursOnly, round to nearest whole hour
  if (hoursOnly) {
    const rounded = Math.round(absHours);
    const prefix = isNegative ? "-" : "";
    return `${prefix}${rounded}h`;
  }

  // Extract whole hours and calculate minutes
  const wholeHours = Math.floor(absHours);
  const minutes = Math.round((absHours - wholeHours) * 60);

  // Handle case where minutes calculation results in 60 (should roll over to hour)
  const adjustedHours = minutes === 60 ? wholeHours + 1 : wholeHours;
  const adjustedMinutes = minutes === 60 ? 0 : minutes;

  // Format the output based on values
  if (adjustedHours === 0 && adjustedMinutes === 0) {
    return "0h";
  }

  const prefix = isNegative ? "-" : "";

  if (adjustedHours === 0) {
    return `${prefix}${adjustedMinutes}m`;
  } else if (adjustedMinutes === 0) {
    return `${prefix}${adjustedHours}h`;
  } else {
    return `${prefix}${adjustedHours}h ${adjustedMinutes}m`;
  }
}

/**
 * Utility function to format multiple timesheet entries' hours
 * @param entries - Array of objects containing logged_hours
 * @returns Total formatted hours and minutes
 */
export function formatTotalHours(entries) {
  // Calculate total hours
  const totalHours = entries.reduce((sum, entry) => {
    const hours = parseFloat(entry.logged_hours);
    return sum + (isNaN(hours) ? 0 : hours);
  }, 0);

  return formatHoursToTime(totalHours);
}

/**
 * Optional: Convert "1 hr 30 min" back to decimal format "1.5"
 * @param timeString - Formatted time string (e.g., "1 hr 30 min")
 * @returns Decimal hours as a string with one decimal place
 */
export function parseTimeToHours(timeString) {
  if (!timeString) return "0";

  const hourMatch = timeString.match(/(\d+)\s*hr/);
  const minuteMatch = timeString.match(/(\d+)\s*min/);

  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

  const decimalHours = hours + minutes / 60;

  // Format to one decimal place and remove trailing zeros
  return decimalHours.toFixed(1).replace(/\.0$/, "");
}

export function formatDaysToTime(days) {
  // Convert input to number if it's a string
  const numericDays = typeof days === "string" ? parseFloat(days) : days;

  // Handle invalid input
  if (numericDays === null || numericDays === undefined || isNaN(numericDays)) {
    return null;
  }

  if (numericDays === 0) {
    return "0";
  }

  const isNegative = numericDays < 0;
  const absDays = Math.floor(Math.abs(numericDays));

  // Break into months (30 days each) and remaining days
  const months = Math.floor(absDays / 30);
  const remainingDays = absDays % 30;

  const prefix = isNegative ? "-" : "";

  const monthSuffix = months === 1 ? "month" : "months";
  const daySuffix = remainingDays === 1 ? "day" : "days";

  const parts = [];
  if (months > 0) parts.push(`${months} ${monthSuffix}`);
  if (remainingDays > 0) parts.push(`${remainingDays} ${daySuffix}`);

  return `${prefix}${parts.join(" ")}`;
}

